import { useState, useRef, useMemo, useEffect, useContext, useCallback } from "react";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { CheckoutContext } from "@swiggy-private/checkout-core";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// types
import { CouponPageType, CouponListingActionPayload } from "@swiggy-private/checkout-core";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { GiftingCartApiResponse, IAppliedCouponCodes } from "@swiggy-private/cart-types";

// helpers
import { getCartId, getRestaurantId } from "@cart-client/helpers/Cart";
import { getPaymentOfferCoupons, getAppliedGiftingCoupons } from "@cart-client/helpers/CouponListing";
import { isEmpty } from "@swiggy-private/js-utils";
import { getAppliedRestaurantCouponCode } from "@swiggy-private/cart-ui";
import { getAppliedPaymentCouponCode } from "@swiggy-private/cart-ui";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import {
    getMROChildCartByRestaurantId,
    getMROChildCarts,
    isMROFlow,
    logMROEventsIntoNR,
} from "@cart-client/helpers/MROFoodCart";

export const useCouponState = () => {
    const { cartState } = useContext(CartContext);
    const { checkoutState } = useContext(CheckoutContext);
    const { couponListingType } = (checkoutState.payload as CouponListingActionPayload) || {};
    const { reviewedCart: parentCartResponse } = cartState;
    const { data: cartFlowData } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(cartFlowData?.cartSource);
    // using ref here to preserve the value across re-renders
    // on back click from MRO coupon listing page, the restaurant id was getting reset
    // not available in payload causing pages to break
    const mroCouponListingRestaurantIdRef = useRef<string | number | undefined>(
        checkoutState.payload?.mroCouponListingRestaurantId || undefined,
    );

    const cartResponse = useMemo(() => {
        if (isMROCart && !isEmpty(mroCouponListingRestaurantIdRef.current)) {
            const childCart = getMROChildCartByRestaurantId(
                parentCartResponse as MROReviewedCart,
                mroCouponListingRestaurantIdRef.current,
            );
            // not treating this as an error, just logging the event
            // as coupon listing breaking the entire cart journey is not a valid handling
            if (isEmpty(childCart)) {
                logMROEventsIntoNR({
                    type: "mro_coupon_listing_rxid_mismatch",
                    restaurantId: String(mroCouponListingRestaurantIdRef.current),
                });
            }
            return { data: childCart } as ReviewedCart;
        }
        return parentCartResponse;
    }, [parentCartResponse, isMROCart]);
    const cartId = getCartId(cartResponse as ReviewedCart);

    const [cartTotal, restaurantId, childCartCount] = useMemo(() => {
        if (isMROCart && !isEmpty(mroCouponListingRestaurantIdRef.current) && !isEmpty(cartResponse)) {
            return [
                cartResponse?.data?.cart_total_shown_to_coupon || 0,
                String(mroCouponListingRestaurantIdRef.current || ""),
                getMROChildCarts(parentCartResponse as MROReviewedCart).length || undefined,
            ];
        }
        return [
            cartResponse?.data?.cart_total_shown_to_coupon || 0,
            cartResponse ? getRestaurantId(cartResponse) : "",
            undefined,
        ];
    }, [cartResponse, isMROCart, parentCartResponse]);

    const [couponCode, setCouponCode] = useState<string>("");
    const [showCrouton, setShowCrouton] = useState(false);

    const isPaymentCouponListingPage = useRef<boolean>(couponListingType === CouponPageType.PAYMENT_COUPON_LISTING);
    const paymentOffersRef = useRef<HTMLDivElement>(null);

    const { data } = useContext(CartFlowContext);
    const isGiftingCouponListing = isGiftingCartSource(data?.cartSource);

    const {
        cards,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        coupons,
        nextBestCoupon,
        resetCouponListingPage,
        tncCoupon,
        hasInitiallyFetched,
        applyCouponClashPopup,
        hasApplyCouponInitiated,
    } = useContext(CouponListingContext);

    const paymentOfferCoupons = useMemo(() => !isEmpty(coupons) && getPaymentOfferCoupons(coupons), [coupons]);
    const hasPaymentOffersCoupon = !isEmpty(paymentOfferCoupons);
    const showBottomSheet = Boolean(isApplyingCoupon || errorMessage.trim() || isRemovingCoupon);

    const appliedGiftingCoupons: IAppliedCouponCodes[] | undefined = useMemo(() => {
        return getAppliedGiftingCoupons(cartResponse?.data as GiftingCartApiResponse);
    }, [cartResponse?.data]);

    const isCouponListingEmpty = useMemo(() => {
        return !isFetching && hasInitiallyFetched && isEmpty(coupons);
    }, [coupons, isFetching, hasInitiallyFetched]);

    const reloadCouponListing = useCallback(() => {
        setCouponCode("");
        resetCouponListingPage();

        const appliedCouponCode = isPaymentCouponListingPage.current
            ? getAppliedPaymentCouponCode(cartResponse?.data)
            : getAppliedRestaurantCouponCode(cartResponse?.data);

        fetchCoupons({
            restaurantId,
            cartValue: cartTotal,
            couponCode: appliedCouponCode,
            appliedCouponCodes: appliedGiftingCoupons,
            subpage: couponListingType,
            childCartCount,
        });
    }, [
        resetCouponListingPage,
        cartResponse?.data,
        fetchCoupons,
        restaurantId,
        cartTotal,
        appliedGiftingCoupons,
        couponListingType,
        childCartCount,
    ]);

    useEffect(() => {
        setShowCrouton(hasPaymentOffersCoupon);
    }, [hasPaymentOffersCoupon]);

    return {
        cartResponse,
        cartTotal,
        restaurantId,
        cartId,
        couponCode,
        setCouponCode,
        showCrouton,
        setShowCrouton,
        isPaymentCouponListingPage,
        paymentOffersRef,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        coupons,
        nextBestCoupon,
        reloadCouponListing,
        tncCoupon,
        paymentOfferCoupons,
        showBottomSheet,
        isCouponListingEmpty,
        isGiftingCouponListing,
        cards,
        appliedGiftingCoupons,
        childCartCount,
        applyCouponClashPopup,
        hasApplyCouponInitiated,
    };
};
